import React, { useState } from "react";
import { Switch } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import {
  ChamberMemberProEventTicketCreditCardPaymentModeEnum,
  ChamberMemberProInvoicePaymentTermsEnum,
  ChamberMemberProManualPaymentMethodTypeEnum,
  ChamberMemberProStripeConnectModeEnum,
  ChamberMemberProStripeConnectStatusEnum,
} from "../../../../../graphql/types";
import { getCmpCreditCardPaymentsReady } from "../../../../../utils/cmpCreditCardPayments";
import {
  CalendarDateInput,
  DecimalNumberInput,
  FieldLabel,
  GlobalSettingsSectionId,
  MoneyInput,
  MonthDayCalendarValue,
  SectionSaveReset,
  SectionSaveResetProps,
  SettingsModal,
  TextInput,
  WholeNumberInput,
  calendarMonthOptions,
  settingsPrimaryButtonClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsSecondaryButtonClassName,
  switchSx,
} from "../common";
import EmailDomainSettingsSection from "../sections/EmailDomainSettingsSection";
import {
  ChamberSettingsFormState,
  PaymentMethod,
  paymentMethods,
} from "../settingsData";

type GlobalSettingsTabProps = {
  settings: ChamberSettingsFormState;
  isSandboxChamber: boolean;
  onChange: (settings: ChamberSettingsFormState) => void;
  sectionControls: Record<GlobalSettingsSectionId, SectionSaveResetProps>;
  onStartStripeConnect: () => void;
  onRefreshStripeConnect: () => void;
  onDeleteStripeConnect: () => void;
  stripeConnectActionLoading: boolean;
  stripeConnectRefreshLoading: boolean;
  stripeConnectDeleteLoading: boolean;
  stripeConnectError: string | null;
  onStartSenderDomainVerification: (values: {
    domain: string;
    senderEmail: string;
  }) => Promise<void>;
  onRefreshSenderDomainStatus: () => Promise<void>;
  onRemoveSenderDomain: () => Promise<void>;
  onSendSenderDomainTestEmail: (recipientEmail: string) => Promise<void>;
  senderDomainActionLoading: boolean;
  senderDomainRefreshLoading: boolean;
  senderDomainRemoveLoading: boolean;
  senderDomainTestEmailLoading: boolean;
  senderDomainError: string | null;
  hasUnsavedSettingsChanges: boolean;
};

const northAmericanTimeZones = [
  "(UTC-10:00) Hawaii-Aleutian Time",
  "(UTC-09:00) Alaska Time",
  "(UTC-08:00) Pacific Time",
  "(UTC-07:00) Mountain Time",
  "(UTC-07:00) Arizona Time",
  "(UTC-06:00) Central Time",
  "(UTC-06:00) Saskatchewan Time",
  "(UTC-05:00) Eastern Time",
  "(UTC-05:00) Indiana Eastern Time",
  "(UTC-04:00) Atlantic Time",
  "(UTC-03:30) Newfoundland Time",
];

// B13 verbatim fix (screenshot 8.57.45 shows "…Eastern Time (US & C…"): the
// mock's standard-zone entries carry the conventional "(US & Canada)" suffix.
// DISPLAY-ONLY — the option VALUES above are the stored settings strings and
// are the lookup keys in src/utils/cmpTimeZones.ts, so only the visible label
// gains the suffix; the saved value is unchanged.
const timeZoneDisplayLabelByValue: Record<string, string> = {
  "(UTC-08:00) Pacific Time": "(UTC-08:00) Pacific Time (US & Canada)",
  "(UTC-07:00) Mountain Time": "(UTC-07:00) Mountain Time (US & Canada)",
  "(UTC-06:00) Central Time": "(UTC-06:00) Central Time (US & Canada)",
  "(UTC-05:00) Eastern Time": "(UTC-05:00) Eastern Time (US & Canada)",
};

const getMonthDayValue = (month: string, day: string) => `${month}|${day}`;

const monthIndexByLabel = calendarMonthOptions.reduce(
  (acc, month, index) => ({
    ...acc,
    [month.label]: index,
  }),
  {} as Record<string, number>
);

const parseMonthDayValue = (monthDayValue: string) => {
  const [month, day] = monthDayValue.split("|");

  return {
    month: month || calendarMonthOptions[0].label,
    day: day || "1",
  };
};

const getFiscalYearEndLabel = (monthDayValue: string) => {
  const { month, day } = parseMonthDayValue(monthDayValue);
  const monthIndex = monthIndexByLabel[month] || 0;
  const parsedDay = Number(day) || 1;
  const startDate = new Date(2025, monthIndex, parsedDay);
  const endDate = new Date(
    startDate.getFullYear() + 1,
    startDate.getMonth(),
    startDate.getDate()
  );
  endDate.setDate(endDate.getDate() - 1);

  return `${calendarMonthOptions[endDate.getMonth()].label} ${endDate.getDate()}`;
};

const chamberGlobalInputClassName =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500";

const chamberGlobalSelectClassName = `${chamberGlobalInputClassName} appearance-none pr-12`;

const GlobalSelectInput = ({
  value,
  onChange,
  options,
  ariaLabel,
  icon,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  icon: "calendar" | "chevron";
}) => {
  const selectOptions = options.some((option) => option.value === value)
    ? options
    : [{ value, label: value }, ...options];

  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={chamberGlobalSelectClassName}
      >
        {selectOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FontAwesomeIcon
        icon={icon === "calendar" ? faCalendar : faChevronDown}
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
      />
    </div>
  );
};

const GlobalReadOnlyDateInput = ({
  value,
  ariaLabel,
}: {
  value: string;
  ariaLabel: string;
}) => (
  <div className="relative">
    <input
      aria-label={ariaLabel}
      value={value}
      readOnly
      className={`${chamberGlobalInputClassName} cursor-default pr-12`}
    />
    <FontAwesomeIcon
      icon={faCalendar}
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
);

const ChamberGlobalField = ({
  title,
  helper,
  children,
  headerClassName = "",
}: {
  title: string;
  helper: string;
  children: React.ReactNode;
  headerClassName?: string;
}) => (
  <div>
    <div className={headerClassName}>
      <h3 className="text-base font-semibold text-gray-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-black">
        {helper}
      </p>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const EnabledSwitchControl = ({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) => (
  <div className="flex shrink-0 items-center gap-3">
    <Switch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      sx={switchSx}
      inputProps={{ "aria-label": ariaLabel }}
    />
    <span
      className={`text-xs font-semibold uppercase ${
        checked ? "text-emerald-600" : "text-gray-500"
      }`}
    >
      {checked ? "Enabled" : "Disabled"}
    </span>
  </div>
);

const setupSectionInputClassName =
  "h-12 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

const screenshotSectionClassName =
  "bg-white px-6 py-4 shadow-none";

const ScreenshotSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className={settingsSectionCardClassName}>
    <div className="px-0">
      <h2 className={`whitespace-pre-wrap ${settingsSectionTitleClassName}`}>
        {title}
      </h2>
      {description && (
        <p className={settingsSectionDescriptionClassName}>{description}</p>
      )}
    </div>
    <div className="mt-7">{children}</div>
  </section>
);

const invoiceTermOptions = [
  {
    label: "Due Upon Receipt",
    value: ChamberMemberProInvoicePaymentTermsEnum.DueUponReceipt,
  },
  {
    label: "30 days from invoice date",
    value: ChamberMemberProInvoicePaymentTermsEnum.Net_30,
  },
  {
    label: "45 days from invoice date",
    value: ChamberMemberProInvoicePaymentTermsEnum.Net_45,
  },
];

type MembershipApplicationCommunicationOptionKey =
  keyof ChamberSettingsFormState["setup"]["membershipApplicationCommunicationOptions"];

const membershipApplicationCommunicationOptionRows: Array<{
  key: MembershipApplicationCommunicationOptionKey;
  label: string;
}> = [
  {
    key: "emailEnabled",
    label:
      "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
  },
  {
    key: "phoneEnabled",
    label: "I prefer communication from the chamber to be done via phone",
  },
  {
    key: "textMessageEnabled",
    label: "I prefer communication from the chamber to be done via text message",
  },
];

// Live preview of the invoice footer sentence. Wording must stay in lockstep
// with cap-backend getCmpInvoiceFooterNote, which renders the real invoices.
const getInvoiceFooterNotePreview = (
  invoicePaymentTerms: ChamberMemberProInvoicePaymentTermsEnum,
  overdueInvoiceLateFeePercentage: string
): string => {
  let paymentTerm = "upon receipt";

  if (invoicePaymentTerms === ChamberMemberProInvoicePaymentTermsEnum.Net_30) {
    paymentTerm = "30 days from the invoice date";
  }

  if (invoicePaymentTerms === ChamberMemberProInvoicePaymentTermsEnum.Net_45) {
    paymentTerm = "45 days from the invoice date";
  }

  const lateFeePercentage = Number.parseFloat(
    overdueInvoiceLateFeePercentage.replace(/[^0-9.]/g, "")
  );
  const lateFeeText =
    lateFeePercentage > 0
      ? `a ${lateFeePercentage}% late payment charge`
      : "a late payment charge";

  return `Payment is due ${paymentTerm}. Overdue balances may be subject to ${lateFeeText}.`;
};

const manualPaymentMethodConfigs = [
  {
    lookupIds: [
      ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
      ChamberMemberProManualPaymentMethodTypeEnum.InvoiceMe,
    ],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
    label: "Company Cheque",
    description: "Enable to allow your chamber to accept company cheque payments.",
  },
  {
    lookupIds: [
      ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
      "e-transfer",
    ],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
    label: "E-Transfer",
    description: "Enable to allow your chamber to accept E-transfer payments.",
  },
  {
    lookupIds: [ChamberMemberProManualPaymentMethodTypeEnum.Cash, "cash"],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.Cash,
    label: "Cash",
    description: "Enable to allow your chamber to accept cash payments.",
  },
  {
    lookupIds: [
      ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
      "direct-deposit",
    ],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
    label: "Direct Deposit",
    description: "Enable to allow your chamber to accept direct deposit payments.",
  },
];

const SUPPORT_EMAIL = "support@chamberperksapp.com";

const creditCardPaymentMethod =
  paymentMethods.find((method) => method.id === "credit-card") ||
  paymentMethods[0];

const GlobalSettingsTab = ({
  settings,
  isSandboxChamber,
  onChange,
  sectionControls,
  onStartStripeConnect,
  onRefreshStripeConnect,
  onDeleteStripeConnect,
  stripeConnectActionLoading,
  stripeConnectRefreshLoading,
  stripeConnectDeleteLoading,
  stripeConnectError,
  onStartSenderDomainVerification,
  onRefreshSenderDomainStatus,
  onRemoveSenderDomain,
  onSendSenderDomainTestEmail,
  senderDomainActionLoading,
  senderDomainRefreshLoading,
  senderDomainRemoveLoading,
  senderDomainTestEmailLoading,
  senderDomainError,
  hasUnsavedSettingsChanges,
}: GlobalSettingsTabProps) => {
  const [templateMethodId, setTemplateMethodId] = useState<string | null>(null);
  // B13 behavioral fix: the Chamber Group Insurance modal edits a local
  // SNAPSHOT of the billing details (taken on open) and only commits to
  // settings on "Save Billing Details" — so Cancel actually discards. A
  // non-null draft doubles as the modal-open flag.
  const [chamberGroupInsuranceBillingDraft, setChamberGroupInsuranceBillingDraft] =
    useState<
      | ChamberSettingsFormState["paymentsAndTaxes"]["chamberGroupInsurancePayment"]["billingDetails"]
      | null
    >(null);
  const [additionalAccountsModalOpen, setAdditionalAccountsModalOpen] =
    useState(false);
  const [additionalAccountsRequest, setAdditionalAccountsRequest] =
    useState("");

  const { setup, membership, paymentsAndTaxes, email } = settings;
  const fiscalYearStartValue = getMonthDayValue(
    setup.fiscalYearStartMonth,
    setup.fiscalYearStartDay
  );
  const fiscalYearEndLabel = getFiscalYearEndLabel(fiscalYearStartValue);
  const stripeConnect = paymentsAndTaxes.stripeConnect;
  const effectiveStripeConnectMode = isSandboxChamber
    ? stripeConnect.mode
    : ChamberMemberProStripeConnectModeEnum.Live;
  const stripeIsTestMode =
    effectiveStripeConnectMode === ChamberMemberProStripeConnectModeEnum.Test;
  const stripeIsConnected =
    stripeConnect.status === ChamberMemberProStripeConnectStatusEnum.Connected &&
    stripeConnect.creditCardPaymentsEnabled;
  const creditCardPaymentsEnabled = stripeConnect.creditCardPaymentsEnabled;
  const eventTicketCreditCardPaymentsReady =
    getCmpCreditCardPaymentsReady(stripeConnect);
  const allEventTicketCreditCardPaymentsEnabled =
    paymentsAndTaxes.eventTicketCreditCardPaymentMode ===
    ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets;
  const nonMemberEventTicketCreditCardPaymentsEnabled =
    paymentsAndTaxes.eventTicketCreditCardPaymentMode !==
    ChamberMemberProEventTicketCreditCardPaymentModeEnum.Disabled;
  const stripeHasConnectedAccount = Boolean(stripeConnect.connectedAccountId);
  const stripeCanDelete =
    isSandboxChamber &&
    stripeIsTestMode &&
    (stripeHasConnectedAccount ||
      stripeConnect.status !==
        ChamberMemberProStripeConnectStatusEnum.NotConnected);
  const stripeCanStartOrContinue =
    !stripeHasConnectedAccount &&
    stripeConnect.status !== ChamberMemberProStripeConnectStatusEnum.Connected;
  const creditCardReadOnlyEnabled = !isSandboxChamber && creditCardPaymentsEnabled;

  const updateSettings = (updater: (current: ChamberSettingsFormState) => ChamberSettingsFormState) => {
    onChange(updater(settings));
  };

  const updateSetup = (
    updater: (
      current: ChamberSettingsFormState["setup"]
    ) => ChamberSettingsFormState["setup"]
  ) => {
    updateSettings((current) => ({
      ...current,
      setup: updater(current.setup),
    }));
  };

  const updateMembership = (
    updater: (
      current: ChamberSettingsFormState["membership"]
    ) => ChamberSettingsFormState["membership"]
  ) => {
    updateSettings((current) => ({
      ...current,
      membership: updater(current.membership),
    }));
  };

  const updatePaymentsAndTaxes = (
    updater: (
      current: ChamberSettingsFormState["paymentsAndTaxes"]
    ) => ChamberSettingsFormState["paymentsAndTaxes"]
  ) => {
    updateSettings((current) => ({
      ...current,
      paymentsAndTaxes: updater(current.paymentsAndTaxes),
    }));
  };

  const updateMembershipApplicationCommunicationOption = ({
    enabled,
    key,
  }: {
    enabled: boolean;
    key: MembershipApplicationCommunicationOptionKey;
  }) => {
    updateSetup((current) => ({
      ...current,
      membershipApplicationCommunicationOptions: {
        ...current.membershipApplicationCommunicationOptions,
        [key]: enabled,
      },
    }));
  };

  const handleFiscalYearStartDateChange = ({
    month,
    day,
  }: MonthDayCalendarValue) => {
    updateSetup((current) => ({
      ...current,
      fiscalYearStartMonth: month,
      fiscalYearStartDay: day,
    }));
  };

  const handleRenewalFixedDateChange = ({
    month,
    day,
  }: MonthDayCalendarValue) => {
    updateSetup((current) => ({
      ...current,
      membershipRenewalFixedDateMonth: month,
      membershipRenewalFixedDateDay: day,
    }));
  };

  const getManualPaymentMethod = (
    config: typeof manualPaymentMethodConfigs[number]
  ): PaymentMethod => {
    const existingMethod = paymentsAndTaxes.manualPaymentMethods.find((method) =>
      config.lookupIds.includes(method.id)
    );
    const fallbackMethod =
      paymentMethods.find((method) => method.id === config.fallbackId) ||
      paymentMethods[1];

    return {
      ...fallbackMethod,
      ...existingMethod,
      id: existingMethod?.id || fallbackMethod.id,
      label: config.label,
      description: config.description,
    };
  };

  const displayedManualPaymentMethods =
    manualPaymentMethodConfigs.map(getManualPaymentMethod);
  const selectedTemplateMethod = displayedManualPaymentMethods.find(
    (method) => method.id === templateMethodId
  );

  const upsertManualPaymentMethod = (
    method: PaymentMethod,
    updater: (method: PaymentMethod) => PaymentMethod
  ) => {
    updatePaymentsAndTaxes((current) => {
      const existingMethod = current.manualPaymentMethods.find(
        (currentMethod) => currentMethod.id === method.id
      );
      const nextMethod = updater(existingMethod || method);

      return {
        ...current,
        manualPaymentMethods: existingMethod
          ? current.manualPaymentMethods.map((currentMethod) =>
              currentMethod.id === method.id ? nextMethod : currentMethod
            )
          : [...current.manualPaymentMethods, nextMethod],
      };
    });
  };

  const updatePaymentTemplate = (
    method: PaymentMethod,
    field: "subject" | "body",
    value: string
  ) => {
    upsertManualPaymentMethod(method, (currentMethod) => ({
      ...currentMethod,
      [field === "subject" ? "emailSubject" : "emailBody"]: value,
    }));
  };

  const invoiceTaxCategoryKeys = Object.keys(
    paymentsAndTaxes.invoiceTaxSettings
  ) as Array<
    keyof ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]
  >;
  const primaryInvoiceTaxSettings =
    paymentsAndTaxes.invoiceTaxSettings.membershipInvoices;
  const taxSettingsEnabled = invoiceTaxCategoryKeys.some(
    (key) => paymentsAndTaxes.invoiceTaxSettings[key].enabled
  );
  const updateAllInvoiceTaxCategories = (
    updater: (
      category: ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
    ) => ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
  ) => {
    updatePaymentsAndTaxes((current) => ({
      ...current,
      invoiceTaxSettings: invoiceTaxCategoryKeys.reduce(
        (nextSettings, key) => ({
          ...nextSettings,
          [key]: updater(current.invoiceTaxSettings[key]),
        }),
        current.invoiceTaxSettings
      ),
    }));
  };

  const renderSetupSwitch = ({
    checked,
    onChange,
    ariaLabel,
    disabled = false,
  }: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    ariaLabel: string;
    disabled?: boolean;
  }) => (
    <div className="flex shrink-0 items-center gap-3">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": ariaLabel }}
      />
      <span
        className={`text-xs font-semibold uppercase ${
          checked ? "text-emerald-600" : "text-gray-500"
        }`}
      >
        {checked ? "ENABLED" : "DISABLED"}
      </span>
    </div>
  );

  const updateAccountAllocation = (
    type: ChamberSettingsFormState["paymentsAndTaxes"]["accountAllocations"][number]["type"],
    field: "accountNumber" | "accountName",
    value: string
  ) => {
    updatePaymentsAndTaxes((current) => ({
      ...current,
      accountAllocations: current.accountAllocations.map((accountAllocation) =>
        accountAllocation.type === type
          ? {
              ...accountAllocation,
              [field]: value,
            }
          : accountAllocation
      ),
    }));
  };

  const updateChamberGroupInsuranceBillingDraft = (
    field: keyof ChamberSettingsFormState["paymentsAndTaxes"]["chamberGroupInsurancePayment"]["billingDetails"],
    value: string
  ) => {
    setChamberGroupInsuranceBillingDraft((currentDraft) =>
      currentDraft
        ? {
            ...currentDraft,
            [field]: value,
          }
        : currentDraft
    );
  };

  const saveChamberGroupInsuranceBillingDraft = () => {
    if (chamberGroupInsuranceBillingDraft) {
      const draftToCommit = chamberGroupInsuranceBillingDraft;
      updatePaymentsAndTaxes((current) => ({
        ...current,
        chamberGroupInsurancePayment: {
          ...current.chamberGroupInsurancePayment,
          billingDetails: draftToCommit,
        },
      }));
    }
    setChamberGroupInsuranceBillingDraft(null);
  };

  const openAdditionalAccountsRequestEmail = () => {
    const bodyText = additionalAccountsRequest.trim()
      ? `Additional account allocation request:\n\n${additionalAccountsRequest.trim()}`
      : "Additional account allocation request:";

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      "Additional account allocation request"
    )}&body=${encodeURIComponent(bodyText)}`;
    setAdditionalAccountsModalOpen(false);
    setAdditionalAccountsRequest("");
  };

  const openAdditionalAccountsModal = () => {
    setAdditionalAccountsModalOpen(true);
  };

  const handleCreditCardToggle = (checked: boolean) => {
    if (creditCardReadOnlyEnabled) {
      return;
    }

    if (checked && !stripeIsConnected && stripeCanStartOrContinue) {
      onStartStripeConnect();
      return;
    }

    if (!checked && stripeCanDelete) {
      onDeleteStripeConnect();
    }
  };

  const renderPaymentMethodCard = ({
    title,
    description,
    enabled,
    onToggle,
    buttonLabel,
    onButtonClick,
    buttonDisabled,
    readOnlyEnabled = false,
    hideButton = false,
  }: {
    title: string;
    description: string;
    enabled: boolean;
    onToggle: (enabled: boolean) => void;
    buttonLabel: string;
    onButtonClick: () => void;
    buttonDisabled?: boolean;
    readOnlyEnabled?: boolean;
    hideButton?: boolean;
  }) => (
    <div className="flex min-h-[180px] flex-col bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold leading-tight text-black">
            {title}
          </h3>
          <p className="mt-4 text-sm leading-5 text-gray-900">
            {description}
          </p>
        </div>
        {readOnlyEnabled ? (
          <span className="inline-flex h-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 px-4 text-xs font-bold uppercase text-emerald-700">
            Enabled
          </span>
        ) : (
          renderSetupSwitch({
            checked: enabled,
            onChange: onToggle,
            ariaLabel: `${title} enabled`,
          })
        )}
      </div>
      {!hideButton && (
        <button
          type="button"
          onClick={onButtonClick}
          disabled={buttonDisabled}
          className={`mt-auto inline-flex h-9 w-fit items-center justify-center rounded-md px-4 text-xs font-bold text-white transition disabled:cursor-not-allowed ${
            buttonDisabled
              ? "bg-gray-500"
              : "bg-cpaGold hover:bg-amber-500"
          }`}
        >
          {buttonLabel}
        </button>
      )}
    </div>
  );

  const renderManualPaymentMethod = (method: PaymentMethod) => (
    <React.Fragment key={method.id}>
      {renderPaymentMethodCard({
        title: method.label,
        description: method.description,
        enabled: method.enabled,
        onToggle: (enabled) =>
          upsertManualPaymentMethod(method, (currentMethod) => ({
            ...currentMethod,
            enabled,
          })),
        buttonLabel: "View and Edit Automated Email Template",
        onButtonClick: () => setTemplateMethodId(method.id),
        buttonDisabled: !method.enabled,
      })}
    </React.Fragment>
  );

  const billingDetails =
    paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails;

  return (
    <div className="space-y-6">
      <section className={settingsSectionCardClassName}>
        <div>
          <h2 className={settingsSectionTitleClassName}>
            Chamber Global Settings
          </h2>
          <p className={settingsSectionDescriptionClassName}>
            Setup your chamber global settings.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-3">
          <ChamberGlobalField
            title="Fiscal Year"
            helper="Set up your membership Fiscal Year"
            headerClassName="xl:min-h-[80px]"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CalendarDateInput
                mode="monthDay"
                ariaLabel="Fiscal year start date"
                value={{
                  month: setup.fiscalYearStartMonth,
                  day: setup.fiscalYearStartDay,
                }}
                onChange={handleFiscalYearStartDateChange}
                inputClassName={`${chamberGlobalInputClassName} w-full`}
              />
              <GlobalReadOnlyDateInput
                ariaLabel="Fiscal year end date"
                value={fiscalYearEndLabel}
              />
            </div>
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Currency"
            helper="Select the currency used for all transactions."
            headerClassName="xl:min-h-[80px]"
          >
            <GlobalSelectInput
              ariaLabel="Currency"
              value={setup.currency}
              onChange={(currency) =>
                updateSetup((current) => ({ ...current, currency }))
              }
              options={[
                {
                  value: "CAD - Canadian Dollar (CAD)",
                  label: "CAD - Canadian Dollar (CAD)",
                },
                {
                  value: "USD - United States Dollar (USD)",
                  label: "USD - United States Dollar (USD)",
                },
              ]}
              icon="chevron"
            />
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Time Zone"
            helper="Select the time zone for all scheduled emails and automation."
            headerClassName="xl:min-h-[80px]"
          >
            <GlobalSelectInput
              ariaLabel="Time zone"
              value={setup.timeZone}
              onChange={(timeZone) =>
                updateSetup((current) => ({ ...current, timeZone }))
              }
              options={northAmericanTimeZones.map((timeZone) => ({
                value: timeZone,
                label: timeZoneDisplayLabelByValue[timeZone] || timeZone,
              }))}
              icon="chevron"
            />
          </ChamberGlobalField>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-3">
          <ChamberGlobalField
            title="Membership Renewal Fix Date"
            helper="If your chamber offers membership renewals on the commencement of your Fiscal Year only please enable and enter date"
            headerClassName="xl:min-h-[152px]"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <EnabledSwitchControl
                checked={setup.membershipRenewalFixedDateEnabled}
                onChange={(membershipRenewalFixedDateEnabled) =>
                  updateSetup((current) => ({
                    ...current,
                    membershipRenewalFixedDateEnabled,
                  }))
                }
                ariaLabel="Enable membership renewal fixed date"
              />
              <div className="w-full sm:flex-1">
                <CalendarDateInput
                  mode="monthDay"
                  ariaLabel="Membership renewal fixed date"
                  value={{
                    month: setup.membershipRenewalFixedDateMonth,
                    day: setup.membershipRenewalFixedDateDay,
                  }}
                  onChange={handleRenewalFixedDateChange}
                  inputClassName={`${chamberGlobalInputClassName} w-full`}
                />
              </div>
            </div>
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Prorated Company Membership Fees"
            helper="If your chamber has enabled Membership Renewals Fix Date only and would like to set up monthly prorated membership fees for new members only please enable."
            headerClassName="xl:min-h-[152px]"
          >
            <EnabledSwitchControl
              checked={membership.globalToggleState.prorate}
              onChange={(prorate) =>
                updateMembership((current) => ({
                  ...current,
                  globalToggleState: {
                    ...current.globalToggleState,
                    prorate,
                  },
                }))
              }
              ariaLabel="Prorated company membership fees"
            />
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Provincial Chamber Of Commerce Fee"
            helper="Enable this option if your chamber collects a provincial Chamber of Commerce membership fee from your members. Enter the fee amount in the field below. Select the checkbox if you want this fee to appear as a separate line item on member invoices."
            headerClassName="xl:min-h-[152px]"
          >
            <div className="space-y-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <EnabledSwitchControl
                  checked={paymentsAndTaxes.provincialChamberFee.enabled}
                  onChange={(enabled) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      provincialChamberFee: {
                        ...current.provincialChamberFee,
                        enabled,
                      },
                    }))
                  }
                  ariaLabel="Provincial chamber fee"
                />
                <div className="w-full sm:flex-1">
                  <MoneyInput
                    value={paymentsAndTaxes.provincialChamberFee.amount}
                    disabled={!paymentsAndTaxes.provincialChamberFee.enabled}
                    onChange={(amount) =>
                      updatePaymentsAndTaxes((current) => ({
                        ...current,
                        provincialChamberFee: {
                          ...current.provincialChamberFee,
                          amount,
                        },
                      }))
                    }
                    placeholder="$0.00"
                    ariaLabel="Provincial chamber fee amount"
                    className={chamberGlobalInputClassName}
                  />
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={
                    paymentsAndTaxes.provincialChamberFee.showSeparateOnInvoice
                  }
                  disabled={!paymentsAndTaxes.provincialChamberFee.enabled}
                  onChange={(event) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      provincialChamberFee: {
                        ...current.provincialChamberFee,
                        showSeparateOnInvoice: event.target.checked,
                      },
                    }))
                  }
                  className="h-5 w-5 rounded border-gray-300 accent-cpaGold disabled:cursor-not-allowed disabled:opacity-50"
                />
                Show separate on invoice
              </label>
            </div>
          </ChamberGlobalField>
        </div>

        <SectionSaveReset {...sectionControls.chamberGlobals} />
      </section>

      <ScreenshotSection
        title="Tax Settings"
        description="Enable and set the tax rate to be charged on all invoices."
      >
        <div className={screenshotSectionClassName}>
          <div className="px-2 sm:px-8">
            <h3 className="text-base font-bold text-black">
              Tax Registration Number
            </h3>
            <p className="mt-5 text-sm leading-6 text-black">
              If the chamber is registered to collect GST/HST and is charging tax, the GST/HST registration number should appear on invoices/receipts when required. Enter the number below.
            </p>
            <input
              value={paymentsAndTaxes.taxRegistrationNumber}
              onChange={(event) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  taxRegistrationNumber: event.target.value,
                }))
              }
              placeholder="Enter Your Chamber Tax Registration Number"
              className={`${setupSectionInputClassName} mt-4`}
            />

            <h3 className="mt-6 text-base font-bold text-black">
              Tax Settings
            </h3>
            <p className="mt-5 max-w-6xl text-base leading-7 text-black">
              If your chamber charges tax on invoices please make sure the setting is Enabled and enter the applicable tax code and percentage rate. If your chamber charges more than one tax, enter each tax rate in the appropriate field. Taxes will show on all invoices.
            </p>
            <div className="mt-8">
              {renderSetupSwitch({
                checked: taxSettingsEnabled,
                onChange: (enabled) =>
                  updateAllInvoiceTaxCategories((category) => ({
                    ...category,
                    enabled,
                  })),
                ariaLabel: "Tax settings enabled",
              })}
              <div className="mt-7 grid max-w-3xl grid-cols-1 gap-x-14 gap-y-4 sm:grid-cols-[1fr_280px_auto]">
                <input
                  value={primaryInvoiceTaxSettings.firstTaxRateLabel}
                  disabled={!taxSettingsEnabled}
                  onChange={(event) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      firstTaxRateLabel: event.target.value,
                    }))
                  }
                  placeholder="Enter Tax Code"
                  className={setupSectionInputClassName}
                />
                <DecimalNumberInput
                  value={primaryInvoiceTaxSettings.firstTaxRatePercentage}
                  disabled={!taxSettingsEnabled}
                  onChange={(firstTaxRatePercentage) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      firstTaxRatePercentage,
                    }))
                  }
                  placeholder="Enter Tax Rate"
                  ariaLabel="First tax rate percentage"
                  className={setupSectionInputClassName}
                />
                <span className="self-center text-base font-semibold text-black">
                  %
                </span>
                <input
                  value={primaryInvoiceTaxSettings.secondTaxRateLabel}
                  disabled={!taxSettingsEnabled}
                  onChange={(event) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      secondTaxRateLabel: event.target.value,
                    }))
                  }
                  placeholder="Enter Tax Code"
                  className={setupSectionInputClassName}
                />
                <DecimalNumberInput
                  value={primaryInvoiceTaxSettings.secondTaxRatePercentage}
                  disabled={!taxSettingsEnabled}
                  onChange={(secondTaxRatePercentage) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      secondTaxRatePercentage,
                    }))
                  }
                  placeholder="Enter Tax Rate"
                  ariaLabel="Second tax rate percentage"
                  className={setupSectionInputClassName}
                />
                <span className="self-center text-base font-semibold text-black">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
        <SectionSaveReset {...sectionControls.taxSettings} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Account Allocation"
        description={
          <>
            Assign an accounting account number to track the various sales options below.
            <br />
            Accounts will not be able to be edited.
          </>
        }
      >
        <div className={screenshotSectionClassName}>
          <div className="overflow-x-auto px-0 sm:px-8">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-[260px_220px_1fr] gap-8 bg-slate-50 px-7 py-4 text-base font-bold text-black">
                <div>Account Allocation</div>
                <div>Account Number</div>
                <div>Account Name</div>
              </div>
              {paymentsAndTaxes.accountAllocations.map((accountAllocation) => (
                <div
                  key={accountAllocation.type}
                  className="grid grid-cols-[260px_220px_1fr] items-center gap-8 px-7 py-10"
                >
                  <div className="text-base font-bold text-black">
                    {accountAllocation.label}
                  </div>
                  <input
                    value={accountAllocation.accountNumber}
                    onChange={(event) =>
                      updateAccountAllocation(
                        accountAllocation.type,
                        "accountNumber",
                        event.target.value
                      )
                    }
                    placeholder="Enter Account #"
                    className={setupSectionInputClassName}
                  />
                  <input
                    value={accountAllocation.accountName}
                    onChange={(event) =>
                      updateAccountAllocation(
                        accountAllocation.type,
                        "accountName",
                        event.target.value
                      )
                    }
                    placeholder="Enter Account Name"
                    className={setupSectionInputClassName}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onMouseDown={openAdditionalAccountsModal}
          onClick={openAdditionalAccountsModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openAdditionalAccountsModal();
            }
          }}
          className="mt-7 flex h-12 w-full items-center justify-center gap-4 rounded-lg bg-white px-4 text-sm font-bold text-black transition hover:bg-amber-50"
        >
          <span className="text-2xl font-normal leading-none">+</span>
          If your chamber would like to add additional accounts please click here to contact support
        </button>
        <SectionSaveReset {...sectionControls.accountAllocation} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Payment Options"
        description="Select which payment methods your chamber will accept."
      >
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          {renderPaymentMethodCard({
            title: creditCardPaymentMethod.label,
            description:
              "Enable to allow your chamber to accept credit card payments.",
            enabled: creditCardPaymentsEnabled,
            onToggle: handleCreditCardToggle,
            buttonLabel: "View and Edit Automated Email Template",
            onButtonClick:
              stripeCanStartOrContinue && !stripeIsConnected
                ? onStartStripeConnect
                : onRefreshStripeConnect,
            buttonDisabled:
              stripeConnectActionLoading ||
              stripeConnectRefreshLoading ||
              stripeConnectDeleteLoading ||
              hasUnsavedSettingsChanges,
            readOnlyEnabled: creditCardReadOnlyEnabled,
            hideButton: creditCardReadOnlyEnabled,
          })}
          {displayedManualPaymentMethods.map(renderManualPaymentMethod)}
          {renderPaymentMethodCard({
            title: "Chamber Group Insurance",
            description:
              "Enable to allow Chamber Group Insurance to pay a member's membership fee.",
            enabled: paymentsAndTaxes.chamberGroupInsurancePayment.enabled,
            onToggle: (enabled) =>
              updatePaymentsAndTaxes((current) => ({
                ...current,
                chamberGroupInsurancePayment: {
                  ...current.chamberGroupInsurancePayment,
                  enabled,
                },
              })),
            buttonLabel: "Enter billing details",
            onButtonClick: () =>
              setChamberGroupInsuranceBillingDraft(billingDetails),
            // Matches the manual-method cards' disabled-button pattern: the
            // billing-details entry point is inert while the option is off.
            buttonDisabled:
              !paymentsAndTaxes.chamberGroupInsurancePayment.enabled,
          })}
        </div>

        {stripeConnectError && (
          <div className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {stripeConnectError}
          </div>
        )}
        {hasUnsavedSettingsChanges && (
          <div className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Save or reset unsaved settings before updating Stripe Connect.
          </div>
        )}

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Credit Card Processing Fee Option
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                If your chamber would like to add a credit card processing fee that will be applied for members using credit card payment option, enable and set percentage. Users who select credit card payment options will be made aware of the fee prior to proceeding. This fee will be added to the total payment amount at the time of checkout.
              </p>
            </div>
            <div className="space-y-5">
              {renderSetupSwitch({
                checked: paymentsAndTaxes.cardProcessingFee.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    cardProcessingFee: {
                      ...current.cardProcessingFee,
                      enabled,
                    },
                  })),
                ariaLabel: "Credit card processing fee",
              })}
              <div className="flex items-center gap-3">
                <DecimalNumberInput
                  value={paymentsAndTaxes.cardProcessingFee.percentage}
                  disabled={!paymentsAndTaxes.cardProcessingFee.enabled}
                  onChange={(percentage) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      cardProcessingFee: {
                        ...current.cardProcessingFee,
                        percentage,
                      },
                    }))
                  }
                  placeholder="0.0"
                  ariaLabel="Credit card processing fee percentage"
                  className={`${setupSectionInputClassName} max-w-[92px]`}
                />
                <span className="text-base font-semibold text-black">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Monthly Membership Payment Administrative Fee Option
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                If your chamber would like to add an administrative transaction fee for processing monthly membership payments that will be applied for members using credit card payment options, enable and set dollar amount per transaction.
              </p>
              <p className="mt-5 max-w-4xl text-sm leading-6 text-black">
                Users who select monthly payment options will be made aware of the <strong>Administrative Transaction</strong> fee prior to proceeding. This fee will be added to the total monthly payment amount.
              </p>
              <p className="mt-6 max-w-4xl text-sm font-bold leading-6 text-black">
                Payment from Chamber Group Insurance will NOT be subject to any monthly Administrative Transaction Fees.
              </p>
            </div>
            <div className="space-y-5">
              {renderSetupSwitch({
                checked:
                  paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    monthlyMembershipAdministrativeFee: {
                      ...current.monthlyMembershipAdministrativeFee,
                      enabled,
                    },
                  })),
                ariaLabel: "Monthly membership administrative fee",
              })}
              <div className="flex items-center gap-3">
                <MoneyInput
                  value={paymentsAndTaxes.monthlyMembershipAdministrativeFee.amount}
                  disabled={
                    !paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled
                  }
                  onChange={(amount) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      monthlyMembershipAdministrativeFee: {
                        ...current.monthlyMembershipAdministrativeFee,
                        amount,
                      },
                    }))
                  }
                  placeholder="$0.00"
                  ariaLabel="Monthly membership administrative fee amount"
                  className={`${setupSectionInputClassName} max-w-[92px]`}
                />
                <span className="text-base font-semibold text-black">
                  /transaction
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Event Ticket Sales - Credit Card Payment Option Only
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                Enable this option to require all event ticket purchases to be
                paid by credit card.
              </p>
            </div>
            {renderSetupSwitch({
              checked: allEventTicketCreditCardPaymentsEnabled,
              disabled: !eventTicketCreditCardPaymentsReady,
              onChange: (enabled) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  eventTicketCreditCardPaymentMode: enabled
                    ? ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                    : ChamberMemberProEventTicketCreditCardPaymentModeEnum.NonMemberTickets,
                })),
              ariaLabel: "Credit card payment required for all event ticket sales",
            })}
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Event Ticket Sales - Credit Card Payment Option For Only
                Non-Members
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                Enable this option to require <strong>non-members</strong> to
                pay for event tickets by credit card.
              </p>
            </div>
            {renderSetupSwitch({
              checked: nonMemberEventTicketCreditCardPaymentsEnabled,
              disabled: !eventTicketCreditCardPaymentsReady,
              onChange: (enabled) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  eventTicketCreditCardPaymentMode: enabled
                    ? current.eventTicketCreditCardPaymentMode ===
                      ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                      ? ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                      : ChamberMemberProEventTicketCreditCardPaymentModeEnum.NonMemberTickets
                    : ChamberMemberProEventTicketCreditCardPaymentModeEnum.Disabled,
                })),
              ariaLabel:
                "Credit card payment required for non-member event ticket sales",
            })}
          </div>
        </div>

        {!eventTicketCreditCardPaymentsReady && (
          <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Connect and enable credit card payments before configuring event
            ticket credit card requirements.
          </p>
        )}
        <SectionSaveReset {...sectionControls.paymentOptions} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Invoicing Options"
        description="Enter and select the invoicing options you want to enable for your chamber."
      >
        <div>
          <h3 className="text-xl font-bold text-black">
            Invoice Start Number
          </h3>
          <p className="mt-2 text-base leading-6 text-black">
            Enter the number you want your Invoices to start from.
          </p>
          <WholeNumberInput
            value={paymentsAndTaxes.invoiceStartNumber}
            onChange={(invoiceStartNumber) =>
              updatePaymentsAndTaxes((current) => ({
                ...current,
                invoiceStartNumber,
              }))
            }
            placeholder="Invoice Start Number"
            ariaLabel="Invoice start number"
            className={`${setupSectionInputClassName} mt-1 max-w-[250px]`}
          />
        </div>

        <div className="mt-12">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_260px] lg:items-start">
            <div>
              <h3 className="text-xl font-bold text-black">
                Membership Discount Option For Internal Use Only
              </h3>
              <p className="mt-3 max-w-6xl text-base leading-6 text-black">
                Enable this option if your chamber would like to apply internal discounts to regular or prorated memberships. Once enabled, a discount description field and discount amount field will appear in the Create Member member-payment section. If both a description and amount are entered, the discount will appear as a separate line item on the invoice. If left blank, no discount will be shown.
              </p>
            </div>
            <div className="pt-6">
              {renderSetupSwitch({
                checked: paymentsAndTaxes.membershipDiscount.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    membershipDiscount: {
                      ...current.membershipDiscount,
                      enabled,
                    },
                  })),
                ariaLabel: "Membership discount option",
              })}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_260px] lg:items-start">
            <div>
              <h3 className="text-xl font-bold text-black">
                One-Time Onboarding Fee For New Members
              </h3>
              <p className="mt-3 max-w-6xl text-base leading-6 text-black">
                Enable this option if your chamber charges a one-time
                onboarding fee for new members. The fee is charged on a new
                membership application, including a returning member who
                reapplies after cancellation, and is not charged on renewals.
              </p>
            </div>
            <div className="pt-1">
              {renderSetupSwitch({
                checked: paymentsAndTaxes.oneTimeOnboardingFee.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    oneTimeOnboardingFee: {
                      ...current.oneTimeOnboardingFee,
                      enabled,
                    },
                  })),
                ariaLabel: "One-time onboarding fee for new members",
              })}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[250px_minmax(0,1fr)_250px]">
            <input
              value={paymentsAndTaxes.oneTimeOnboardingFee.code}
              disabled={!paymentsAndTaxes.oneTimeOnboardingFee.enabled}
              onChange={(event) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  oneTimeOnboardingFee: {
                    ...current.oneTimeOnboardingFee,
                    code: event.target.value,
                  },
                }))
              }
              placeholder="Enter Code"
              aria-label="One-time onboarding fee code"
              className={setupSectionInputClassName}
            />
            <input
              value={paymentsAndTaxes.oneTimeOnboardingFee.description}
              disabled={!paymentsAndTaxes.oneTimeOnboardingFee.enabled}
              onChange={(event) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  oneTimeOnboardingFee: {
                    ...current.oneTimeOnboardingFee,
                    description: event.target.value,
                  },
                }))
              }
              placeholder="One-Time Onboarding Fee"
              aria-label="One-time onboarding fee description"
              className={setupSectionInputClassName}
            />
            <MoneyInput
              value={paymentsAndTaxes.oneTimeOnboardingFee.amount}
              disabled={!paymentsAndTaxes.oneTimeOnboardingFee.enabled}
              onChange={(amount) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  oneTimeOnboardingFee: {
                    ...current.oneTimeOnboardingFee,
                    amount,
                  },
                }))
              }
              placeholder="Enter Amount"
              ariaLabel="One-time onboarding fee amount"
              className={setupSectionInputClassName}
            />
          </div>
          <p className="mt-3 text-sm leading-6 text-gray-700">
            This fee is taxed normally, but membership discounts and proration
            do not reduce it.
          </p>
        </div>

        <div className="mt-14">
          <h3 className="text-xl font-bold text-black">
            Invoice Payment Terms and Invoice Footer Note
          </h3>
          <p className="mt-2 text-base leading-6 text-black">
            The information below will appear at bottom of all invoices.
          </p>
          <p className="mt-7 text-base font-bold text-black">
            {getInvoiceFooterNotePreview(
              paymentsAndTaxes.invoicePaymentTerms,
              paymentsAndTaxes.overdueInvoiceLateFeePercentage
            )}
          </p>

          <div className={`${screenshotSectionClassName} mt-10`}>
            <div className="px-0 sm:px-8">
              <h4 className="text-base font-bold text-black">
                Invoice Payment Terms
              </h4>
              <p className="mt-4 text-base leading-6 text-black">
                Set up your payment terms which will show on all invoices not paid when payment is due so members know your expected payment timeline.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-3">
                {invoiceTermOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-5 text-base text-black"
                  >
                    <input
                      type="radio"
                      checked={
                        paymentsAndTaxes.invoicePaymentTerms === option.value
                      }
                      onChange={() =>
                        updatePaymentsAndTaxes((current) => ({
                          ...current,
                          invoicePaymentTerms: option.value,
                        }))
                      }
                      className="h-5 w-5 border-gray-400 accent-cpaGold focus:outline-cpaGold"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className={`${screenshotSectionClassName} mt-5`}>
            <div className="px-0 sm:px-8">
              <h4 className="text-base font-bold text-black">
                Overdue Invoice
              </h4>
              <p className="mt-4 text-base leading-6 text-black">
                Set up your chamber's late fee rate. If left blank the invoice footer will simply say "may be subject to a late payment charge".
              </p>
              <div className="mt-4 flex max-w-xs items-center gap-3">
                <DecimalNumberInput
                  value={paymentsAndTaxes.overdueInvoiceLateFeePercentage}
                  onChange={(overdueInvoiceLateFeePercentage) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      overdueInvoiceLateFeePercentage,
                    }))
                  }
                  placeholder="00.00"
                  ariaLabel="Overdue invoice late fee percentage"
                  className={setupSectionInputClassName}
                />
                <span className="text-base font-semibold text-black">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
        <SectionSaveReset {...sectionControls.invoicingOptions} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Set Up How Members Communicate with your Chamber"
        description="Check the options you would like to appear on the online membership application for members to select."
      >
        <div className="space-y-5">
          {membershipApplicationCommunicationOptionRows.map((option) => (
            <label
              key={option.key}
              className="flex cursor-pointer items-start gap-4 text-base leading-6 text-black"
            >
              <input
                type="checkbox"
                checked={
                  setup.membershipApplicationCommunicationOptions[option.key]
                }
                onChange={(event) =>
                  updateMembershipApplicationCommunicationOption({
                    enabled: event.target.checked,
                    key: option.key,
                  })
                }
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-500 accent-cpaGold focus:outline-cpaGold"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <SectionSaveReset {...sectionControls.memberCommunication} />
      </ScreenshotSection>

      <EmailDomainSettingsSection
        settings={email}
        onStartSenderDomainVerification={onStartSenderDomainVerification}
        onRefreshSenderDomainStatus={onRefreshSenderDomainStatus}
        onRemoveSenderDomain={onRemoveSenderDomain}
        onSendSenderDomainTestEmail={onSendSenderDomainTestEmail}
        senderDomainActionLoading={senderDomainActionLoading}
        senderDomainRefreshLoading={senderDomainRefreshLoading}
        senderDomainRemoveLoading={senderDomainRemoveLoading}
        senderDomainTestEmailLoading={senderDomainTestEmailLoading}
        senderDomainError={senderDomainError}
        hasUnsavedSettingsChanges={hasUnsavedSettingsChanges}
      />

      {additionalAccountsModalOpen && (
        <SettingsModal
          title="Request Additional Account Allocations"
          description={`Tell us what additional accounts your chamber needs. This will open an email addressed to ${SUPPORT_EMAIL}.`}
          onClose={() => setAdditionalAccountsModalOpen(false)}
          panelClassName="max-w-2xl"
        >
          <div className="space-y-5">
            <FieldLabel label="Request Details">
              <textarea
                value={additionalAccountsRequest}
                onChange={(event) =>
                  setAdditionalAccountsRequest(event.target.value)
                }
                placeholder="Describe the additional account allocations you would like to add."
                className="min-h-[160px] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100"
              />
            </FieldLabel>
            <div className="flex flex-col justify-end gap-3 border-t border-gray-200 pt-5 sm:flex-row">
              <button
                type="button"
                onClick={() => setAdditionalAccountsModalOpen(false)}
                className={`${settingsSecondaryButtonClassName} min-w-[140px]`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={openAdditionalAccountsRequestEmail}
                className={`${settingsPrimaryButtonClassName} min-w-[200px]`}
              >
                Email Support
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {chamberGroupInsuranceBillingDraft && (
        <SettingsModal
          title="Chamber Group Insurance"
          description="Enter billing details for invoice creation (No invoice will be sent, payment to be received and applied to invoice only)"
          onClose={() => setChamberGroupInsuranceBillingDraft(null)}
          panelClassName="max-w-2xl"
        >
          <div className="space-y-5">
            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Company Name <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.companyName}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "companyName",
                    event.target.value
                  )
                }
                placeholder="Enter Company Name"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <div>
              <span className="text-sm font-bold text-gray-900">
                Company Address <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.streetAddress}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "streetAddress",
                    event.target.value
                  )
                }
                placeholder="Street Number and Name"
                className={`${setupSectionInputClassName} mt-2`}
              />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  value={chamberGroupInsuranceBillingDraft.city}
                  onChange={(event) =>
                    updateChamberGroupInsuranceBillingDraft(
                      "city",
                      event.target.value
                    )
                  }
                  placeholder="City"
                  className={setupSectionInputClassName}
                />
                <input
                  value={chamberGroupInsuranceBillingDraft.postalCode}
                  onChange={(event) =>
                    updateChamberGroupInsuranceBillingDraft(
                      "postalCode",
                      event.target.value
                    )
                  }
                  placeholder="Postal Code"
                  className={setupSectionInputClassName}
                />
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Name <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactName}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactName",
                    event.target.value
                  )
                }
                placeholder="Enter Contact Name"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Title
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactTitle}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactTitle",
                    event.target.value
                  )
                }
                placeholder="Enter Title"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Email <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactEmail}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactEmail",
                    event.target.value
                  )
                }
                placeholder="Enter Contact Email"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Phone Number <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.phoneNumber}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "phoneNumber",
                    event.target.value
                  )
                }
                placeholder="Enter Phone Number"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5">
              <button
                type="button"
                onClick={() => setChamberGroupInsuranceBillingDraft(null)}
                className={settingsSecondaryButtonClassName}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveChamberGroupInsuranceBillingDraft}
                className={settingsPrimaryButtonClassName}
              >
                Save Billing Details
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {selectedTemplateMethod && (
        <SettingsModal
          title={`Edit ${selectedTemplateMethod.label} email template`}
          description="Customize the payment instructions members receive when this method is used."
          onClose={() => setTemplateMethodId(null)}
          panelClassName="flex min-h-[75vh] max-h-[85vh] flex-col"
          bodyClassName="flex flex-1 overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            <FieldLabel label="Subject">
              <TextInput
                value={selectedTemplateMethod.emailSubject}
                onChange={(value) =>
                  updatePaymentTemplate(selectedTemplateMethod, "subject", value)
                }
              />
            </FieldLabel>
            <div className="flex min-h-0 flex-1 flex-col">
              <span className="block text-sm font-semibold text-gray-900">
                Email verbiage
              </span>
              <textarea
                value={selectedTemplateMethod.emailBody}
                onChange={(event) =>
                  updatePaymentTemplate(
                    selectedTemplateMethod,
                    "body",
                    event.target.value
                  )
                }
                className="mt-2 min-h-0 flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setTemplateMethodId(null)}
                className={settingsPrimaryButtonClassName}
              >
                Save Template
              </button>
            </div>
          </div>
        </SettingsModal>
      )}
    </div>
  );
};

export default GlobalSettingsTab;
